/**
 * Document Editor Toolbar Component
 *
 * Full-featured toolbar for PDF document editing with:
 * - Pagination and zoom controls
 * - Search functionality
 * - Download PDF
 * - Annotate/Forms toggle with conditional tools
 * - Counterparty permissions dropdown
 * - "View Comments" button
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styles from './DocumentEditorToolbar.module.scss';
import { ReactComponent as SidebarCollapseIcon } from '../../shared/icons/sidebarcollapse.svg';
import { ReactComponent as SidebarExpandIcon } from '../../shared/icons/sidebarexpand.svg';
import { ReactComponent as DownloadIcon } from '../../shared/icons/download.svg';
import { ReactComponent as CommentIcon } from '../../shared/icons/icon-comment.svg';
import { ReactComponent as AddTextIcon } from '../../shared/icons/add-text-icon.svg';
import { ReactComponent as SearchIcon } from '../../shared/icons/search-icon.svg';
import { ReactComponent as CheckmarkIcon } from '../../shared/icons/checkmark-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../shared/icons/chevron-down.svg';
import Toggle from '../toggle';
import { CounterpartyPermissionLevel, COUNTERPARTY_PERMISSION_LABELS, canEditPages } from '../../types/counterparty-permissions';

export type AnnotationTool = 'none' | 'comment' | 'strikeout' | 'freetext';
export type FormFieldTool = 'none' | 'textfield' | 'checkbox';

export interface DocumentEditorToolbarProps {
  documentId: string;
  documentTitle: string;
  lastSaved?: string;

  thumbnailsVisible?: boolean;
  onToggleThumbnails?: () => void;
  showThumbnailToggle?: boolean;

  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;

  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;

  onSearch?: (query: string) => void;
  searchResultsCount?: number;
  currentSearchResult?: number;
  onNextSearchResult?: () => void;
  onPreviousSearchResult?: () => void;

  onDownloadPdf: () => void;

  activeAnnotationTool?: AnnotationTool;
  onSelectAnnotationTool?: (tool: AnnotationTool) => void;

  formBuilderActive: boolean;
  onToggleFormBuilder: () => void;
  activeFormFieldTool?: FormFieldTool;
  onSelectFormFieldTool?: (tool: FormFieldTool) => void;

  counterpartyPermissionLevel: CounterpartyPermissionLevel; // What counterparties can do (for dropdown display)
  activePermissionLevel?: CounterpartyPermissionLevel; // What current user can do (for UI controls)
  canAccessFormsToggle?: boolean; // Whether to show Annotate/Forms toggle (creator team with edit permission only)
  onChangePermissions: (level: CounterpartyPermissionLevel) => void;
  canEditPermissions: boolean;

  commentsVisible?: boolean;
  onToggleComments?: () => void;
}

export const DocumentEditorToolbar: React.FC<DocumentEditorToolbarProps> = ({
  thumbnailsVisible = false,
  onToggleThumbnails,
  showThumbnailToggle = false,
  currentPage,
  totalPages,
  onPageChange,
  zoom,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onSearch,
  searchResultsCount = 0,
  currentSearchResult = 0,
  onNextSearchResult,
  onPreviousSearchResult,
  onDownloadPdf,
  activeAnnotationTool = 'none',
  onSelectAnnotationTool,
  formBuilderActive,
  onToggleFormBuilder,
  activeFormFieldTool = 'none',
  onSelectFormFieldTool,
  counterpartyPermissionLevel,
  activePermissionLevel, // If not provided, falls back to counterpartyPermissionLevel
  canAccessFormsToggle = false,
  onChangePermissions,
  canEditPermissions,
  commentsVisible = false,
  onToggleComments,
}) => {
  const zoomPercentage = Math.round(zoom * 100);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageInputValue, setPageInputValue] = useState(currentPage.toString());
  const [searchDropdownVisible, setSearchDropdownVisible] = useState(false);
  const [permissionsDropdownVisible, setPermissionsDropdownVisible] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const permissionsRef = useRef<HTMLDivElement>(null);

  // Sync local page input value with currentPage prop
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Determine which permission level to use for UI controls
  const effectiveLevel = activePermissionLevel || counterpartyPermissionLevel;

  // Check effective permissions for UI controls
  const counterpartyCanEditPages = canEditPages(effectiveLevel);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(searchQuery);
      }
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, onSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (permissionsRef.current && !permissionsRef.current.contains(event.target as Node)) {
        setPermissionsDropdownVisible(false);
      }
    };

    if (permissionsDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [permissionsDropdownVisible]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleAnnotationToolClick = useCallback(
    (tool: AnnotationTool) => {
      if (onSelectAnnotationTool) {
        onSelectAnnotationTool(activeAnnotationTool === tool ? 'none' : tool);
      }
    },
    [activeAnnotationTool, onSelectAnnotationTool]
  );

  const handleFormFieldToolClick = useCallback(
    (tool: FormFieldTool) => {
      if (onSelectFormFieldTool) {
        onSelectFormFieldTool(activeFormFieldTool === tool ? 'none' : tool);
      }
    },
    [activeFormFieldTool, onSelectFormFieldTool]
  );

  const handlePermissionChange = useCallback(
    (newLevel: CounterpartyPermissionLevel) => {
      onChangePermissions(newLevel);
      setPermissionsDropdownVisible(false);
    },
    [onChangePermissions]
  );

  return (
    <div className={styles.toolbar}>
      {showThumbnailToggle && onToggleThumbnails && (
        <>
          <button
            className={styles.thumbnailToggle}
            onClick={onToggleThumbnails}
            title={thumbnailsVisible ? 'Collapse Sidebar' : 'Expand Sidebar'}
          >
            {thumbnailsVisible ? <SidebarCollapseIcon /> : <SidebarExpandIcon />}
          </button>
          <div className={styles.separator}></div>
        </>
      )}

      <button
        className={styles.iconButton}
        onClick={onDownloadPdf}
        title="Download PDF"
      >
        <DownloadIcon />
      </button>
      <div className={styles.separator}></div>

      <div className={styles.pageControls}>
        <span className={styles.pageInfo}>
          <input
            type="text"
            inputMode="numeric"
            className={styles.pageInput}
            value={pageInputValue}
            onChange={(e) => {
              // Allow free typing - just update local state
              setPageInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault(); // Prevent form submission
                const page = parseInt(pageInputValue, 10);
                if (!isNaN(page) && page >= 1 && page <= totalPages) {
                  onPageChange(page);
                  (e.target as HTMLInputElement).blur();
                } else {
                  // Invalid input - restore current page
                  setPageInputValue(currentPage.toString());
                }
              }
            }}
            onBlur={() => {
              const page = parseInt(pageInputValue, 10);
              if (!isNaN(page) && page >= 1 && page <= totalPages) {
                // Valid page - navigate
                onPageChange(page);
              } else {
                // Invalid - restore current page
                setPageInputValue(currentPage.toString());
              }
            }}
          />
          <span className={styles.pageSeparator}>/</span>
          <span className={styles.totalPages}>{totalPages}</span>
        </span>
      </div>
      <div className={styles.separator}></div>

      <div className={styles.zoomControls}>
        <button
          className={styles.controlButton}
          onClick={onZoomOut}
          title="Zoom Out"
        >
          −
        </button>
        <button
          className={styles.zoomButton}
          onClick={onResetZoom}
          title="Reset Zoom (100%)"
        >
          {zoomPercentage}%
        </button>
        <button
          className={styles.controlButton}
          onClick={onZoomIn}
          title="Zoom In"
        >
          +
        </button>
      </div>
      <div className={styles.separator}></div>

      {canEditPermissions && (
        <>
          <div className={styles.permissionsDropdown} ref={permissionsRef}>
            <button
              className={styles.permissionsButton}
              onClick={() => setPermissionsDropdownVisible(!permissionsDropdownVisible)}
            >
              <span>Counterparty Access</span>
              <ChevronDownIcon className={styles.chevron} />
            </button>

            {permissionsDropdownVisible && (
              <div className={styles.permissionsMenu}>
                {Object.entries(COUNTERPARTY_PERMISSION_LABELS).map(([value, label]) => (
                  <button
                    key={value}
                    className={`${styles.permissionsMenuItem} ${value === counterpartyPermissionLevel ? styles.selected : ''}`}
                    onClick={() => handlePermissionChange(value as CounterpartyPermissionLevel)}
                  >
                    <span>{label}</span>
                    {value === counterpartyPermissionLevel && (
                      <CheckmarkIcon className={styles.checkmark} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={styles.separator}></div>
        </>
      )}

      {/* Only show Forms toggle if user can access it (creator team with edit permission) */}
      {canAccessFormsToggle && (
        <>
          <div className={styles.modeToggleWrapper}>
            <span className={styles.modeLabel}>Annotate</span>
            <Toggle
              checked={formBuilderActive}
              onChange={onToggleFormBuilder}
              disabled={false}
              isCustomDisabledState={false}
              className="react-toggle-blue"
            />
            <span className={styles.modeLabel}>Forms</span>
          </div>
          <div className={styles.separator}></div>
        </>
      )}

      {!formBuilderActive && onSelectAnnotationTool && (
        <>
          {/* Comment tool - available to all users regardless of permission level */}
          <button
            className={`${styles.iconButton} ${activeAnnotationTool === 'comment' ? styles.active : ''}`}
            onClick={() => handleAnnotationToolClick('comment')}
            title="Add Comment"
          >
            <CommentIcon />
          </button>

          {/* Strikeout and Free Text - only for Full Editing users */}
          {counterpartyCanEditPages && (
            <>
              <button
                className={`${styles.iconButton} ${activeAnnotationTool === 'strikeout' ? styles.active : ''}`}
                onClick={() => handleAnnotationToolClick('strikeout')}
                title="Strikeout"
              >
                <span className={styles.strikeoutIcon}>S</span>
              </button>
              <button
                className={`${styles.iconButton} ${activeAnnotationTool === 'freetext' ? styles.active : ''}`}
                onClick={() => handleAnnotationToolClick('freetext')}
                title="Add Text"
              >
                <AddTextIcon />
              </button>
            </>
          )}
          <div className={styles.separator}></div>
        </>
      )}

      {formBuilderActive && onSelectFormFieldTool && (
        <>
          <button
            className={`${styles.iconButton} ${activeFormFieldTool === 'textfield' ? styles.active : ''}`}
            onClick={() => handleFormFieldToolClick('textfield')}
            title="Text Field"
          >
            <span className={styles.textFieldIcon}>T</span>
          </button>
          <button
            className={`${styles.iconButton} ${activeFormFieldTool === 'checkbox' ? styles.active : ''}`}
            onClick={() => handleFormFieldToolClick('checkbox')}
            title="Checkbox"
          >
            <span className={styles.checkboxFieldIcon}>✓</span>
          </button>
          <div className={styles.separator}></div>
        </>
      )}

      {onSearch && (
        <>
          <div className={styles.searchDropdownWrapper}>
            <button
              className={`${styles.iconButton} ${searchDropdownVisible ? styles.active : ''}`}
              onClick={() => setSearchDropdownVisible(!searchDropdownVisible)}
              title="Search in document"
            >
              <SearchIcon />
            </button>

            {searchDropdownVisible && (
              <div className={styles.searchDropdownPanel}>
                <div className={styles.searchPanelHeader}>
                  <input
                    type="text"
                    className={styles.searchPanelInput}
                    placeholder="Search in document..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    className={styles.searchPanelCloseButton}
                    onClick={() => {
                      handleClearSearch();
                      setSearchDropdownVisible(false);
                    }}
                    title="Close search"
                  >
                    ✕
                  </button>
                </div>

                {searchResultsCount > 0 && (
                  <div className={styles.searchPanelResults}>
                    <span className={styles.searchPanelCount}>
                      {currentSearchResult} of {searchResultsCount}
                    </span>
                    <div className={styles.searchPanelNav}>
                      <button
                        type="button"
                        className={styles.searchPanelNavButton}
                        onClick={onPreviousSearchResult}
                        disabled={searchResultsCount === 0}
                        title="Previous result"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className={styles.searchPanelNavButton}
                        onClick={onNextSearchResult}
                        disabled={searchResultsCount === 0}
                        title="Next result"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )}

                {searchQuery && searchResultsCount === 0 && (
                  <div className={styles.searchPanelNoResults}>
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.separator}></div>
        </>
      )}

      <div className={styles.spacer}></div>

      {onToggleComments && (
        <button
          className={`${styles.viewCommentsButton} ${commentsVisible ? styles.active : ''}`}
          onClick={onToggleComments}
          title={commentsVisible ? 'Hide Comments' : 'View Comments'}
        >
          <span>{commentsVisible ? 'Hide Comments' : 'View Comments'}</span>
        </button>
      )}
    </div>
  );
};
